import React from "react";
import { SbTable, SbCheckbox, SbEmpty } from "uiCore";
import { ROLE_STATUS_CLOSED, ROLE_STATUS_OPEN, NESTED_TABLE_SCROLL_HEIGHT } from "../talentMobility.constants";
import { getFilters } from "../RedeploymentCenterV2/RedeploymentCenterV2.utils";
import { NestedTableWrapperStyled } from "./TalentMobilityManagerV2.styles";
import RolePanelCollapse from "../components/RolePanelCollapse";

// The accordion panel a role row (useTalentMobilityManagerColumns) expands
// into — the per-employee rows that role's outer Open/Discoverable counts
// roll up. Same checkbox columns TalentMobilityManagerV2's table used to
// render itself before grouping by role; updateRoleField is unchanged (still
// keyed per employee id), so toggling one here recomputes both this row and
// its role's counts on the next render. Filters + filterSearch are the same
// column-search pattern the outer role table uses (see useTalentMobilityManagerColumns).
// Neither the Name cell nor the row itself navigates — Role Detail is
// reached from the outer table's Title link instead (one entry point per
// role, not one per employee).
const TalentMobilityRoleEmployeesTable = ({ role, updateRoleField, isOpen }) => {
  const { employees } = role;

  // A vacant role (role.isVacant, from groupTableRowsByRole) has one row —
  // the API's placeholder for "published, nobody ACTIVE on it" — with no
  // real Employee ID/Name/checkboxes to show. A blank row with two
  // checkboxes reads as a data-loading bug, not "nobody's assigned yet", so
  // this renders the actual message instead of the table.
  if (role.isVacant) {
    return (
      <RolePanelCollapse isOpen={isOpen}>
        <NestedTableWrapperStyled>
          <div className="nested-title">Employees in this role</div>
          <SbEmpty description="No employees are currently assigned to this role." />
        </NestedTableWrapperStyled>
      </RolePanelCollapse>
    );
  }

  const columns = [
    {
      title: "Employee ID",
      dataIndex: "employeeId",
      filters: getFilters(employees, "employeeId"),
      filterSearch: true,
      onFilter: (value, record) => record.employeeId === value,
      render: (v) => v || "—",
    },
    {
      title: "Name",
      dataIndex: "name",
      filters: getFilters(employees, "name"),
      filterSearch: true,
      onFilter: (value, record) => record.name === value,
    },
    {
      title: "Open",
      dataIndex: "status",
      align: "center",
      sorter: (a, b) => Number(a.status === ROLE_STATUS_OPEN) - Number(b.status === ROLE_STATUS_OPEN),
      // align: "center" only centers the body cells — a sortable header's
      // title + sort icon sit in their own flex box untouched by `align`
      // (see NestedTableWrapperStyled's .tm-col-header-center rule), same
      // fix as the outer role table (useTalentMobilityManagerColumns).
      onHeaderCell: () => ({ className: "tm-col-header-center" }),
      render: (status, record) => (
        <SbCheckbox
          checked={status === ROLE_STATUS_OPEN}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) =>
            updateRoleField(record.id, record.roleId, "status", e.target.checked ? ROLE_STATUS_OPEN : ROLE_STATUS_CLOSED)
          }
        />
      ),
    },
    {
      title: "Discoverable",
      dataIndex: "discoverable",
      align: "center",
      sorter: (a, b) => Number(a.discoverable) - Number(b.discoverable),
      onHeaderCell: () => ({ className: "tm-col-header-center" }),
      render: (discoverable, record) => (
        <SbCheckbox
          checked={discoverable}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => updateRoleField(record.id, record.roleId, "discoverable", e.target.checked)}
        />
      ),
    },
  ];

  return (
    <RolePanelCollapse isOpen={isOpen}>
      <NestedTableWrapperStyled>
        <div className="nested-title">Employees in this role</div>
        <SbTable
          columns={columns}
          dataSource={employees}
          rowKey="id"
          pagination={false}
          scroll={{ y: NESTED_TABLE_SCROLL_HEIGHT }}
        />
      </NestedTableWrapperStyled>
    </RolePanelCollapse>
  );
};

export default TalentMobilityRoleEmployeesTable;
